/**
 * TokenSearch — Debounced search input for token discovery.
 *
 * Debounces onChange by 300ms. Only triggers search when
 * the input has 2+ characters or is empty (to clear).
 *
 * Story 3.4 — Task 4.1
 */

import { useEffect, useRef, useState } from "react";

import { cn } from "../../lib/cn";

type TokenSearchProps = {
	value: string;
	onChange: (value: string) => void;
};

export function TokenSearch({ value, onChange }: TokenSearchProps) {
	const [localValue, setLocalValue] = useState<string>(value);
	const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	// Sync external value changes (e.g., URL-driven clear)
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	function handleChange(newValue: string) {
		setLocalValue(newValue);

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		timerRef.current = setTimeout(() => {
			if (newValue.length >= 2 || newValue === "") {
				onChange(newValue);
			}
		}, 300);
	}

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, []);

	return (
		<div className="relative">
			<svg
				className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-pastel-gray"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth={2}
				stroke="currentColor"
				aria-hidden="true"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
				/>
			</svg>
			<input
				type="text"
				placeholder="Search tokens..."
				value={localValue}
				onChange={(e) => handleChange(e.target.value)}
				className={cn(
					"w-full rounded-lg border border-pastel-border bg-white pl-10 pr-3 py-2 text-sm text-pastel-black placeholder:text-pastel-gray",
					"focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-blue/40 focus:border-pastel-vibrant-blue",
					"transition-colors"
				)}
			/>
		</div>
	);
}
